import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  LayoutDashboard, PlusCircle, Database, Package, Activity,
  LogOut, Battery, ChevronRight, User, Zap, Boxes, UserPlus
} from "lucide-react";

const navItems = [
  { path: "/dashboard", label: "Accueil", shortLabel: "Accueil", icon: LayoutDashboard },
  { path: "/cells/create", label: "Creer des cellules", shortLabel: "Creer", icon: PlusCircle },
  { path: "/library", label: "Bibliotheque", shortLabel: "Cellules", icon: Database },
  { path: "/packs", label: "Packs", shortLabel: "Packs", icon: Package },
  { path: "/generate-bloc", label: "Generate Bloc", shortLabel: "Bloc", icon: Boxes },
  { path: "/live", label: "Mesure en direct", shortLabel: "Mesure", icon: Activity },
];

const pageTitles = {
  "/dashboard": "Tableau de bord",
  "/cells/create": "Creer des cellules",
  "/library": "Bibliotheque",
  "/packs": "Gestion des packs",
  "/live": "Mesure en direct",
};

/* ─── Desktop Sidebar ─────────────────────────────────────────────────── */
function DesktopSidebar() {
  const { user, logout } = useAuth();

  return (
    <aside className="hidden lg:flex w-64 bg-cyber-surface border-r border-neon-blue/10 flex-col shrink-0 relative overflow-hidden" data-testid="sidebar">
      {/* Scan line effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-blue/20 to-transparent animate-scan-line" />
      </div>

      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 relative">
        <div className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center bg-black border border-neon-blue/30 neon-glow-blue">
          <img src="/logo-lxhev.jpg" alt="LX HEV" className="w-9 h-9 object-contain" />
        </div>
        <div>
          <h1 className="font-heading font-black text-lg tracking-tight text-white leading-none neon-text-blue">PROJET LX HEV</h1>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neon-blue/60">Batteries hybrides</span>
        </div>
      </div>

      <Separator className="bg-neon-blue/10" />

      <nav className="flex-1 py-4 px-3 space-y-1" data-testid="sidebar-nav">
        {navItems.map(({ path, label, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group ${
                isActive
                  ? "bg-neon-blue-muted text-neon-blue border-l-2 border-neon-blue neon-glow-blue"
                  : "text-muted-foreground hover:text-white hover:bg-cyber-elevated border-l-2 border-transparent"
              }`
            }
            data-testid={`nav-${path.replace(/\//g, "-").slice(1)}`}
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span>{label}</span>
            <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink
            to="/register"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border-l-2 ${
                isActive ? "bg-neon-blue-muted text-neon-blue border-neon-blue" : "text-muted-foreground hover:text-white hover:bg-cyber-elevated border-transparent"
              }`
            }
            data-testid="nav-register"
          >
            <UserPlus className="w-4 h-4 shrink-0" />
            <span>Ajouter un utilisateur</span>
          </NavLink>
        )}
      </nav>

      <Separator className="bg-neon-blue/10" />

      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-neon-blue/20 to-neon-purple/20 border border-neon-blue/30 rounded-lg flex items-center justify-center">
            <span className="font-mono text-xs text-neon-blue font-bold">{user?.name?.charAt(0)?.toUpperCase() || "?"}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <Badge variant="outline" className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0 ${user?.role === "admin" ? "border-neon-purple/30 text-neon-purple" : "border-neon-cyan/30 text-neon-cyan"}`}>{user?.role}</Badge>
          </div>
        </div>
        <Button variant="outline" size="sm" className="w-full text-xs border-neon-blue/20 hover:border-neon-red hover:text-neon-red bg-transparent" onClick={logout} data-testid="logout-button">
          <LogOut className="w-3 h-3 mr-2" />Deconnexion
        </Button>
      </div>
    </aside>
  );
}

/* ─── Mobile Bottom TabBar ────────────────────────────────────────────── */
function MobileTabBar() {
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-cyber-surface/95 backdrop-blur-xl border-t border-neon-blue/20 safe-area-bottom" data-testid="mobile-tabbar">
      <div className="flex items-stretch justify-around h-16">
        {navItems.map(({ path, shortLabel, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 gap-0.5 py-1 transition-all duration-200 relative ${
                isActive ? "text-neon-blue" : "text-muted-foreground active:text-white"
              }`
            }
            data-testid={`tab-${path.replace(/\//g, "-").slice(1)}`}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-neon-blue to-neon-purple rounded-full shadow-[0_0_8px_rgba(59,130,246,0.6)]" />
                )}
                <Icon className={`w-5 h-5 ${isActive ? "drop-shadow-[0_0_6px_rgba(59,130,246,0.6)]" : ""}`} strokeWidth={isActive ? 2.5 : 1.5} />
                <span className="text-[10px] font-medium leading-none">{shortLabel}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/* ─── Mobile Header ───────────────────────────────────────────────────── */
function MobileHeader({ title }) {
  const { user, logout } = useAuth();

  return (
    <header className="lg:hidden h-12 border-b border-neon-blue/10 bg-cyber-surface/95 backdrop-blur-xl flex items-center px-4 shrink-0" data-testid="mobile-header">
      <div className="flex items-center gap-2">
        <img src="/logo-lxhev.jpg" alt="LX HEV" className="w-6 h-6 rounded-md object-contain bg-black border border-neon-blue/20" />
        <span className="font-heading font-black text-sm tracking-tight text-white neon-text-blue">LX HEV</span>
      </div>
      <h2 className="ml-3 font-heading font-bold text-sm text-muted-foreground truncate">{title}</h2>
      <div className="ml-auto flex items-center gap-2">
        <Badge variant="outline" className="text-[9px] font-mono uppercase tracking-wider border-neon-blue/20 text-neon-blue bg-neon-blue-muted px-1.5 py-0 flex items-center gap-1">
          <span className="w-1 h-1 bg-neon-blue rounded-full animate-pulse-neon" />
          Actif
        </Badge>
        <button onClick={logout} className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-neon-red transition-colors" data-testid="mobile-logout-button">
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </header>
  );
}

/* ─── Desktop Header ──────────────────────────────────────────────────── */
function DesktopHeader({ title }) {
  const { user } = useAuth();

  return (
    <header className="hidden lg:flex h-14 border-b border-neon-blue/10 bg-cyber-surface/80 backdrop-blur-xl items-center px-6 shrink-0" data-testid="main-header">
      <h2 className="font-heading font-bold text-lg text-white tracking-tight">{title}</h2>
      <div className="ml-auto flex items-center gap-3">
        <Badge variant="outline" className="text-[10px] font-mono uppercase tracking-wider border-neon-blue/20 text-neon-blue bg-neon-blue-muted px-2 py-0.5 flex items-center gap-1.5 neon-glow-blue">
          <Zap className="w-3 h-3 animate-pulse-neon" />
          Systeme actif
        </Badge>
        <span className="text-xs text-muted-foreground font-mono">{user?.email}</span>
      </div>
    </header>
  );
}

/* ─── Main Layout ─────────────────────────────────────────────────────── */
export default function Layout() {
  const location = useLocation();
  const currentTitle = pageTitles[location.pathname] || "Projet LX HEV";

  return (
    <div className="flex h-screen overflow-hidden bg-background cyber-grid-bg">
      <DesktopSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <DesktopHeader title={currentTitle} />
        <MobileHeader title={currentTitle} />

        <main className="flex-1 overflow-auto p-4 lg:p-6 pb-20 lg:pb-6">
          <Outlet />
        </main>
      </div>

      <MobileTabBar />
    </div>
  );
}
